import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Printer, FileSignature, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/api";
import { personMatch } from "../lib/searchText";

const AR_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = String(ym).split("-");
  return `${AR_MONTHS[parseInt(m, 10) - 1] || m} ${y}`;
};

export const TargetAcksLogDialog = ({ open, onOpenChange }) => {
  const [items, setItems] = useState(null);
  const del = async (it) => {
    if (!window.confirm(`حذف إقرار «${it.employee_name || it.employee_id}» لشهر ${it.month}؟`)) return;
    try { await api.deleteTargetWarning(it.id); setItems((xs) => xs.filter((x) => x.id !== it.id)); toast.success("تم الحذف"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.targetWarnings()
      .then((r) => setItems(r.items || []))
      .catch(() => toast.error("تعذّر تحميل سجل الإقرارات"))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = (items || []).filter((it) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return personMatch(it, s)
      || String(it.month || "").includes(s);
  });

  const printAll = () => {
    if (!filtered.length) { toast.warning("لا توجد إقرارات للطباعة"); return; }
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const cards = filtered.map((it) => `
      <div class="card">
        <div class="hd"><b>${esc(it.employee_name || it.employee_id)}</b>
          <span class="muted">#${esc(it.employee_number)}${it.branch ? " · " + esc(it.branch) : ""}</span>
          <span class="tag">${esc(monthLabel(it.month))}</span>
          <span class="tag red">إنذار #${esc(it.warning_number)}</span></div>
        <div class="row">الطلبات المحقّقة: <b>${esc(it.orders)}</b> &nbsp;/&nbsp; الهدف: <b>${esc(it.target)}</b></div>
        <div class="just"><b>المبرّر:</b> ${esc(it.justification || "—")}</div>
        ${it.signature_id ? `<div class="sig"><div class="muted">التوقيع:</div><img src="${fileUrl(it.signature_id)}"/></div>` : ""}
        ${it.acknowledged_at ? `<div class="muted">بتاريخ: ${esc(String(it.acknowledged_at).slice(0, 10))}</div>` : ""}
      </div>`).join("");
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>سجل إقرارات التارقت</title>
      <style>@page{size:A4;margin:12mm}body{font-family:SaudiRiyal,'IBM Plex Sans Arabic',Arial,sans-serif;color:#0f172a}
      h1{text-align:center;font-size:18px;margin:0 0 14px}
      .card{border:1px solid #cbd5e1;border-radius:10px;padding:10px 12px;margin-bottom:10px;page-break-inside:avoid}
      .hd{font-size:13px;margin-bottom:4px}.muted{color:#94a3b8;font-size:11px}
      .tag{display:inline-block;background:#f1f5f9;border-radius:999px;padding:1px 8px;font-size:11px;margin-right:4px}
      .tag.red{background:#fee2e2;color:#b91c1c}
      .row{font-size:12px;margin:3px 0}.just{font-size:12px;background:#f8fafc;border-radius:6px;padding:6px;margin:5px 0;white-space:pre-wrap}
      .sig img{max-height:90px;border:1px solid #e2e8f0;border-radius:6px;background:#fff;margin-top:3px}</style></head>
      <body><h1>سجل إقرارات عدم تحقيق التارقت</h1>${cards}
      <script>window.onload=function(){setTimeout(function(){window.print()},500)}</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("فعّل النوافذ المنبثقة للطباعة"); return; }
    w.document.write(html); w.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto font-body" dir="rtl" data-testid="target-acks-log-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-blue-600" /> سجل الإقرارات
          </DialogTitle>
          <DialogDescription className="text-right">جميع إقرارات المندوبين بعدم تحقيق التارقت مع التواقيع.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="acks-search-input" placeholder="ابحث بالاسم أو الرقم أو الشهر..." value={q}
              onChange={(e) => setQ(e.target.value)} className="pr-9" />
          </div>
          <Button variant="outline" size="sm" onClick={printAll} disabled={!filtered.length} data-testid="acks-print-all-button" className="gap-1.5">
            <Printer className="h-4 w-4" /> طباعة
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !filtered.length ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500" data-testid="acks-empty">
            لا توجد إقرارات مسجّلة بعد.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((it) => (
              <div key={it.id} data-testid={`ack-row-${it.employee_id}-${it.month}`} className="rounded-2xl border border-slate-200 p-4 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 truncate">{it.employee_name || it.employee_id}</div>
                    <div className="text-xs text-slate-400 font-mono-nums">#{it.employee_number}{it.branch ? ` · ${it.branch}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="rounded-full bg-slate-100 text-slate-600 px-2.5 py-1 text-xs font-bold">{monthLabel(it.month)}</span>
                    <span className="rounded-full bg-red-100 text-red-700 px-2.5 py-1 text-xs font-bold">إنذار #{it.warning_number}</span>
                    {it.cleared_by_manager && <span className="rounded-full bg-amber-100 text-amber-700 px-2.5 py-1 text-xs font-bold">رُفع بواسطة الإدارة</span>}
                    <button onClick={() => del(it)} className="rounded-full p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" title="حذف" data-testid={`ack-delete-${it.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="mt-2.5 grid grid-cols-2 gap-2">
                  <div className="rounded-lg bg-slate-50 p-2 text-center"><div className="text-xs text-slate-400">الطلبات</div><div className="font-heading font-extrabold font-mono-nums text-slate-800">{it.orders}</div></div>
                  <div className="rounded-lg bg-slate-50 p-2 text-center"><div className="text-xs text-slate-400">الهدف</div><div className="font-heading font-extrabold font-mono-nums text-blue-600">{it.target}</div></div>
                </div>
                <div className="mt-2.5">
                  <div className="text-xs font-semibold text-slate-500 mb-1">المبرّر</div>
                  <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-2.5 whitespace-pre-wrap leading-relaxed">{it.justification || "—"}</p>
                </div>
                {it.signature_id && (
                  <div className="mt-2.5">
                    <div className="text-xs font-semibold text-slate-500 mb-1">التوقيع</div>
                    <img src={fileUrl(it.signature_id)} alt="signature" data-testid={`ack-signature-${it.employee_id}-${it.month}`}
                      className="max-h-28 rounded-lg border border-slate-200 bg-white" />
                  </div>
                )}
                {it.acknowledged_at && <div className="mt-2 text-[11px] text-slate-400 font-mono-nums">بتاريخ: {String(it.acknowledged_at).slice(0, 10)}</div>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
